import React from "react";
import { Box, Paper, Skeleton, Stack } from "@mui/material";

/**
 * Skeleton loader for the Admin Loans page. Mirrors the layout
 * (back link → header → KPI cards → filters card → tabs → loan cards) so the
 * loading state has the same shape as the loaded page.
 */
export default function AdminLoansSkeleton() {
  return (
    <Box>
      {/* Back link */}
      <Skeleton variant="text" width={140} height={28} sx={{ mb: 2 }} />

      {/* Header row */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: 1.5 }} />
          <Box>
            <Skeleton variant="text" width={220} height={36} />
            <Skeleton variant="text" width={320} height={20} />
          </Box>
        </Stack>
        <Skeleton variant="rounded" width={150} height={40} sx={{ borderRadius: 1.25 }} />
      </Stack>

      {/* KPI cards */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mb: 3 }}>
        {[0, 1, 2, 3].map((i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{
              flex: 1,
              p: 2.5,
              borderRadius: 1.5,
              border: (theme) => `1px solid ${theme.palette.border.light}`,
            }}
          >
            <Skeleton variant="rounded" width={40} height={40} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="text" width="50%" height={18} />
          </Paper>
        ))}
      </Stack>

      {/* Filters card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ flex: 1 }}>
              <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1.25 }} />
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* Tab pills */}
      <Skeleton
        variant="rounded"
        width={520}
        height={44}
        sx={{ borderRadius: 999, mb: 3 }}
      />

      {/* Loan cards */}
      <Stack spacing={2}>
        {[0, 1, 2].map((i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 1.5,
              border: (theme) => `1px solid ${theme.palette.border.light}`,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Skeleton variant="circular" width={56} height={56} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={220} height={24} />
                <Skeleton variant="text" width={160} height={22} />
                <Skeleton variant="text" width={280} height={20} sx={{ mb: 2 }} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.5, sm: 4 }}>
                  {[0, 1, 2, 3].map((c) => (
                    <Box key={c} sx={{ minWidth: 120 }}>
                      <Skeleton variant="text" width={80} height={18} />
                      <Skeleton variant="text" width={100} height={22} />
                    </Box>
                  ))}
                </Stack>
              </Box>
              <Skeleton variant="rounded" width={130} height={40} sx={{ borderRadius: 1.25 }} />
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
